import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Sparkles,
  ArrowRight,
  Users,
  Ticket,
  Calendar as CalIcon,
  Clock,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return iso;
  }
}

export default function EventsSection() {
  const [experiences, setExperiences] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/experiences`)
      .then((r) => setExperiences(r.data))
      .catch(() => setExperiences([]));
  }, [refresh]);

  const weeks = useMemo(() => {
    const grouped = {};
    experiences.forEach((e) => {
      grouped[e.week] = grouped[e.week] || [];
      grouped[e.week].push(e);
    });
    return Object.entries(grouped).map(([w, items]) => ({
      week: Number(w),
      items: items.sort((a, b) => a.date.localeCompare(b.date)),
    }));
  }, [experiences]);

  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8 items-end">
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[#f5f0ea] font-semibold">
              Weekend Experience Series
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 tracking-tighter text-white">
              12 moments.{" "}
              <span className="text-[#A7D38B] italic">4 weekends.</span> One
              October to remember.
            </h2>
          </div>
          <p className="lg:col-span-4 text-neutral-400">
            Hand-crafted Friday, Saturday and Sunday drops. 15 slots per
            experience. Book yours before they close.
          </p>
        </div>

        {/* Intro price banner */}
        <div
          data-testid="intro-price-banner"
          className="mb-10 rounded-2xl glass border border-[#A7D38B]/25 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
        >
          <div className="flex items-center gap-2.5 text-[#A7D38B] flex-shrink-0">
            <Ticket size={16} />
            <span className="text-[10px] uppercase tracking-[0.28em] font-semibold">
              Introductory offer
            </span>
          </div>
          <div className="hidden sm:block h-6 w-px bg-white/10" />
          <p className="text-sm text-neutral-200 leading-relaxed flex-1">
            <span className="text-white font-semibold">₹199 per person</span>{" "}
            for any experience · Valid till{" "}
            <span className="text-[#A7D38B] font-semibold">25 Oct 2026</span>{" "}
            · 15 slots each
          </p>
        </div>

        <div className="space-y-10">
          {weeks.map(({ week, items }) => (
            <div key={week} data-testid={`week-${week}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="font-display text-2xl font-semibold text-white">
                  Week {week}
                </span>
                <span className="text-xs text-neutral-500">
                  {items[0] && formatDate(items[0].date)} —{" "}
                  {items[items.length - 1] &&
                    formatDate(items[items.length - 1].date)}
                </span>
                <div className="flex-1 divider" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {items.map((exp, i) => (
                  <ExperienceCard
                    key={exp.id}
                    exp={exp}
                    index={i}
                    onBook={() => setSelected(exp)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <BookingDialog
        exp={selected}
        onClose={() => setSelected(null)}
        onSuccess={() => setRefresh((r) => r + 1)}
      />
    </section>
  );
}

function ExperienceCard({ exp, index, onBook }) {
  const remaining = exp.slots_remaining;
  const isSoldOut = remaining <= 0;
  const isLow = remaining > 0 && remaining <= 4;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      whileHover={{ y: -4 }}
      className="rounded-3xl bg-neutral-900 border border-white/5 hover:border-[#A7D38B]/40 p-5 flex flex-col transition-colors"
      data-testid={`experience-card-${exp.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-3xl">
          {exp.emoji}
        </div>
        <div className="flex flex-col items-end gap-1.5 text-right">
          {isSoldOut ? (
            <span className="rounded-full bg-neutral-800 text-neutral-400 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1">
              Sold out
            </span>
          ) : isLow ? (
            <span className="rounded-full bg-[#EF4444]/90 text-white text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 animate-pulseGlow">
              Only {remaining} left
            </span>
          ) : (
            <span className="rounded-full bg-[#62794d]/20 border border-[#A7D38B]/30 text-[#A7D38B] text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1">
              {remaining} slots
            </span>
          )}
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            ₹{exp.price} · pp
          </div>
        </div>
      </div>

      <h3 className="font-display text-xl font-semibold text-white mt-4">
        {exp.title}
      </h3>
      <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed flex-1">
        {exp.description}
      </p>

      <div className="mt-4 flex items-center gap-4 text-[11px] text-neutral-400 border-t border-white/5 pt-3">
        <span className="inline-flex items-center gap-1.5">
          <CalIcon size={12} className="text-[#A7D38B]" />
          {exp.day} · {formatDate(exp.date)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock size={12} className="text-[#A7D38B]" />
          {exp.time}
        </span>
      </div>

      <button
        onClick={onBook}
        disabled={isSoldOut}
        data-testid={`experience-book-${exp.id}`}
        className="mt-4 inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#62794d] text-[#f5f0ea] font-semibold py-2.5 text-sm hover:bg-[#7a9261] active:scale-[0.98] transition disabled:bg-neutral-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
      >
        {isSoldOut ? "Sold Out" : "Book Slot"}
        {!isSoldOut && <ArrowRight size={14} />}
      </button>
    </motion.div>
  );
}

function BookingDialog({ exp, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [guests, setGuests] = useState("1");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (exp) {
      setName("");
      setPhone("");
      setGuests("1");
      setEmail("");
      setConfirmed(null);
    }
  }, [exp?.id]);

  if (!exp) return null;

  const maxGuests = Math.min(exp.slots_remaining, 8);

  const submit = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Add your name and contact number");
      return;
    }
    const g = parseInt(guests, 10) || 1;
    if (g > exp.slots_remaining) {
      toast.error(`Only ${exp.slots_remaining} slot(s) left`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/experience-bookings`, {
        experience_id: exp.id,
        name,
        phone,
        email: email || null,
        guests: g,
      });
      setConfirmed({ ...data, guests: g, total: g * exp.price });
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Booking failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const ref = confirmed?.id?.slice(0, 8).toUpperCase() || "—";

  return (
    <Dialog open={!!exp} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-neutral-950 border border-white/10 text-neutral-100 max-w-md rounded-3xl p-6 sm:p-7"
      >
        {!confirmed ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#62794d]/20 border border-[#A7D38B]/30 flex items-center justify-center text-2xl">
                  {exp.emoji}
                </div>
                <div>
                  <DialogTitle className="font-display text-2xl font-bold tracking-tight text-white">
                    {exp.title}
                  </DialogTitle>
                  <DialogDescription className="text-neutral-400 text-xs mt-0.5">
                    {exp.day} · {formatDate(exp.date)} · {exp.time} · ₹{exp.price} pp
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/8 p-4 text-xs text-neutral-300 leading-relaxed">
              {exp.description}
            </div>

            <div className="mt-5 space-y-3">
              <Field label="Your name">
                <input
                  data-testid="exp-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Aanya M."
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#A7D38B] focus:outline-none px-3 py-2.5 text-sm text-white placeholder:text-neutral-500"
                />
              </Field>
              <Field label="Phone">
                <input
                  data-testid="exp-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98xxxxxx21"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#A7D38B] focus:outline-none px-3 py-2.5 text-sm text-white placeholder:text-neutral-500"
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  data-testid="exp-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#A7D38B] focus:outline-none px-3 py-2.5 text-sm text-white placeholder:text-neutral-500"
                />
              </Field>
              <Field label={`Guests · ${exp.slots_remaining} slot${exp.slots_remaining !== 1 ? "s" : ""} left`}>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from({ length: Math.min(maxGuests, 6) }).map((_, i) => {
                    const n = i + 1;
                    const active = String(n) === guests;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setGuests(String(n))}
                        data-testid={`exp-guests-${n}`}
                        className={`w-10 h-10 rounded-lg text-sm font-semibold border transition ${
                          active
                            ? "bg-[#62794d] text-[#f5f0ea] border-[#A7D38B]"
                            : "bg-white/[0.04] text-neutral-300 border-white/10 hover:border-white/25"
                        }`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total · pay at venue</div>
                <div className="font-display text-2xl font-bold text-white">
                  ₹{(parseInt(guests, 10) || 1) * exp.price}
                </div>
              </div>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="exp-book-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] px-6 py-3 font-semibold text-sm hover:bg-[#7a9261] active:scale-[0.97] transition disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Booking…
                  </>
                ) : (
                  <>
                    Confirm Booking <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="exp-confirmation"
            className="text-center py-2"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 220 }}
              className="inline-flex w-14 h-14 rounded-full bg-[#62794d] items-center justify-center mb-3"
            >
              <CheckCircle2 size={28} className="text-[#f5f0ea]" />
            </motion.div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight text-white">
              You're in.
            </DialogTitle>
            <DialogDescription className="text-neutral-400 text-sm mt-1">
              {confirmed.guests} slot{confirmed.guests > 1 ? "s" : ""} booked for {exp.title}
            </DialogDescription>

            <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/8 p-4 text-left space-y-2 text-sm">
              <Row label="Reference">
                <span className="font-mono text-[#A7D38B]">{ref}</span>
              </Row>
              <Row label="When">
                {exp.day} · {formatDate(exp.date)} · {exp.time}
              </Row>
              <Row label="Guests">{confirmed.guests}</Row>
              <Row label="Total · pay at venue">
                ₹{confirmed.total.toLocaleString()}
              </Row>
            </div>

            <p className="text-[11px] text-neutral-500 mt-4 leading-relaxed">
              A confirmation email is on its way. Arrive 10 minutes early. Free cancellation up to 24h before.
            </p>

            <button
              onClick={onClose}
              data-testid="exp-close-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-5 py-2.5 text-xs font-semibold text-white hover:bg-white/10 transition"
            >
              Done
            </button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <span className="text-white text-right">{children}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 mb-1.5 font-semibold">
        {label}
      </div>
      {children}
    </div>
  );
}
